import type { ReactNode } from 'react'
import { useRef } from 'react'
import { View } from 'react-native'
import type Animated from 'react-native-reanimated'

import { Header } from '@/src/elements/header'
import type { TEventOriginParam } from '@/src/general/constants/routes'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useBackHandlePress } from '@/src/general/hooks/useBackHandlePress'
import { PUBLICATION_TRACKING_SOURCE, TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'

import { useCommunityQuery } from '../../../services/useCommunityQuery'
import { useCommunitySubscriptionsQuery } from '../../../services/useCommunitySubscriptionsQuery'
import { usePublicationsOfCommunityQuery } from '../../../services/usePublicationsOfCommunityQuery'
import { PublicationsList } from '../../components/PublicationsList'
import { useTodayPublication } from '../../hooks/useTodayPublication'
import { CommunityActionButton } from '../CommunityScreen/CommunityActionButton/CommunityActionButton'

type TCommunityPublicationsScreenProps = TEventOriginParam & {
  id: number
}

export const CommunityPublicationsScreen = ({ id, origin }: TCommunityPublicationsScreenProps): ReactNode => {
  const listRef = useRef<Animated.FlatList | null>(null)
  const { routes } = useRoutes()

  const communityQuery = useCommunityQuery(id)
  const community = communityQuery.data
  const communityRoute = community ? routes.community({ id, slug: community.slug, origin }) : routes.communities()
  const { handleBackPress } = useBackHandlePress(communityRoute)

  const { activeSubscription: subscription } = useCommunitySubscriptionsQuery(id, community?.lifetime)
  const publications = usePublicationsOfCommunityQuery({
    id,
    type: community?.lifetime,
    enabled: !!communityQuery.isSuccess,
  })

  const { todayPublication, isSearching: isSearchingTodayPublication } = useTodayPublication(publications)
  const todayPublicationIndex =
    todayPublication && publications.data ? publications.data.findIndex((p) => p.id === todayPublication.id) : -1

  const listProps = {
    publications: publications.data,
    isPending: communityQuery.isLoading || isSearchingTodayPublication,
    isFetchingNextPage: publications.isFetchingNextPage,
    hasNextPage: publications.hasNextPage,
    fetchNextPage: publications.fetchNextPage,
  }

  return (
    <View testID={TEST_ID.COMMUNITY.PUBLICATIONS_SCREEN} className="relative h-full flex-1">
      <Header
        leftButtons={[
          {
            startIcon: ICON.ARROW_LEFT,
            onPress: handleBackPress,
            type: 'icon',
            colors: 'primary',
            variant: 'faded',
            size: 'md',
            isIconOnly: true,
          },
        ]}
        className="absolute z-10"
      />
      <PublicationsList
        {...listProps}
        listRef={listRef}
        community={community}
        subscription={subscription}
        featuredPublicationId={todayPublication?.id}
        autoScrollIndex={todayPublicationIndex}
        trackingSource={TRACKING_SOURCE.COMMUNITY_PUBLICATIONS_PAGE}
      />
      <CommunityActionButton
        communityId={id}
        community={community}
        course="page"
        origin={origin}
        source={TRACKING_SOURCE.COMMUNITY_PUBLICATIONS_PAGE}
        publicationTrackingSource={PUBLICATION_TRACKING_SOURCE.COMMUNITY_READ_MORE}
      />
    </View>
  )
}
